import { isDefined } from "@clipboard-health/util-ts";
import { APP_V2_USER_EVENTS, logEvent } from "@src/appV2/lib/analytics";
import { useLogEffect } from "@src/appV2/lib/analytics/useLogEffect";
import {
  toShiftPendingPostBookingRequirementsInput,
  usePendingPostBookingRequirementsForShifts,
} from "@src/appV2/redesign/Documents/api/wrappers/usePendingPostBookingRequirementsForShifts";
import { buildWorkplaceDocumentsPath } from "@src/appV2/redesign/Documents/paths";
import { type ShiftFormattedPayBreakdown } from "@src/appV2/redesign/Shift/types";
import { getShiftCurrentStage } from "@src/appV2/redesign/Shift/WorkerShift/getShiftCurrentStage";
import { HighlightedWorkerShiftCard } from "@src/appV2/redesign/Shift/WorkerShift/HighlightedCard";
import { HighlightedWorkerInterviewCard } from "@src/appV2/redesign/Shift/WorkerShift/HighlightedWorkerInterviewCard";
import { type Bookings } from "@src/appV2/redesign/WorkerShifts/api/useFetchPaginatedInterviews";
import { useGetShiftState } from "@src/appV2/Shifts/Shift/ShiftState/api/useGetShiftState";
import { type Shift, shiftPeriodToTimeSlot } from "@src/appV2/Shifts/Shift/types";
import { useMemo } from "react";
import { generatePath, useHistory } from "react-router-dom";

import { type WorkersForOverlappingShiftSlots } from "../../WorkWithFriends/hooks/useGetWorkersForOverlappingShiftSlots";
import { SHIFT_DETAILS_FULL_PATH } from "../path";

interface HighlightedBookingSectionProps {
  highlightedBooking?: Bookings;
  workersForOverlappingShiftSlot?: WorkersForOverlappingShiftSlots;
  shiftPayBreakdown?: ShiftFormattedPayBreakdown;
  currentDate: Date;
}

function HighlightedShiftBooking(props: {
  shift: Shift;
  shiftPayBreakdown?: ShiftFormattedPayBreakdown;
  currentDate: Date;
  workersForOverlappingShiftSlot?: WorkersForOverlappingShiftSlots;
}) {
  const { shift, shiftPayBreakdown, currentDate, workersForOverlappingShiftSlot } = props;

  const history = useHistory();

  const { data: shiftState } = useGetShiftState(
    { shiftStateDependencies: { _id: shift._id } },
    { enabled: true }
  );

  const workplaceId = shift.facility.userId;
  const qualification = shift.agentReq;

  const shiftInputs = useMemo(() => [toShiftPendingPostBookingRequirementsInput(shift)], [shift]);

  const { resultsByShiftId } = usePendingPostBookingRequirementsForShifts(shiftInputs);
  const pendingPostBookingDocumentsCount = resultsByShiftId[shift._id]?.count ?? 0;

  const hasPendingPostBookingDocuments =
    isDefined(workplaceId) && pendingPostBookingDocumentsCount > 0;

  useLogEffect(
    APP_V2_USER_EVENTS.POST_BOOKING_DOCUMENTS_BOOKING_CARD_BUTTON_SHOWN,
    {
      shiftId: shift._id,
      workplaceId,
      qualification,
      count: pendingPostBookingDocumentsCount,
      surface: "highlighted",
    },
    { enabled: hasPendingPostBookingDocuments }
  );

  const onCompleteDocumentsClick = hasPendingPostBookingDocuments
    ? () => {
        logEvent(APP_V2_USER_EVENTS.POST_BOOKING_DOCUMENTS_BOOKING_CARD_BUTTON_CLICKED, {
          shiftId: shift._id,
          workplaceId,
          qualification,
          count: pendingPostBookingDocumentsCount,
          surface: "highlighted",
        });
        history.push(
          buildWorkplaceDocumentsPath({
            workplaceId,
            qualification,
            shiftId: shift._id,
            postBookingRequirementsFilter: "include-only",
          })
        );
      }
    : undefined;

  return (
    <HighlightedWorkerShiftCard
      href={generatePath(SHIFT_DETAILS_FULL_PATH.replace(":shiftId", shift._id))}
      shiftId={shift._id}
      workplaceName={shift.facility.name}
      workplaceId={workplaceId ?? undefined}
      workersForOverlappingShiftSlot={workersForOverlappingShiftSlot}
      workplaceTimezone={shift.facility.tmz ?? ""}
      workerTimezone={new Intl.DateTimeFormat().resolvedOptions().timeZone}
      shiftStart={shift.start}
      shiftEnd={shift.end}
      shiftPosition={qualification}
      shiftHourlyPay={shiftPayBreakdown?.hourlyPay}
      shiftTotalPay={shiftPayBreakdown?.workedBreakPay?.totalPay ?? shiftPayBreakdown?.totalPay}
      shiftTotalWithBoostPay={
        shiftPayBreakdown?.workedBreakPay?.totalWithBoostPay ?? shiftPayBreakdown?.totalWithBoostPay
      }
      shiftStage={getShiftCurrentStage(shift)}
      shiftTimeSlot={shiftPeriodToTimeSlot[shift.name]}
      targetArrivalTime={shift.computedArrival?.targetArrivalTime}
      currentDate={currentDate}
      isInstantPayShift={shift.isInstantPay ?? false}
      isMissedPunchRequestEnabled={shiftState?.metadata.isMissedPunchRequestEnabled}
      pendingPostBookingDocumentsCount={pendingPostBookingDocumentsCount}
      onCompleteDocumentsClick={onCompleteDocumentsClick}
    />
  );
}

export function HighlightedBookingSection(props: HighlightedBookingSectionProps) {
  const { highlightedBooking, shiftPayBreakdown, currentDate, workersForOverlappingShiftSlot } =
    props;

  if (!isDefined(highlightedBooking)) {
    return null;
  }

  if (highlightedBooking.type === "shift") {
    return (
      <HighlightedShiftBooking
        shift={highlightedBooking.data}
        shiftPayBreakdown={shiftPayBreakdown}
        currentDate={currentDate}
        workersForOverlappingShiftSlot={workersForOverlappingShiftSlot}
      />
    );
  }

  if (highlightedBooking.type === "interview") {
    return (
      <HighlightedWorkerInterviewCard
        interviewId={highlightedBooking.data.id}
        start={highlightedBooking.data.start}
        end={highlightedBooking.data.end}
        status={highlightedBooking.data.status}
        placementId={highlightedBooking.data.placementId}
        workerTypes={highlightedBooking.data.placement?.workerTypes ?? []}
        workplaceName={highlightedBooking.data.workplace?.name ?? ""}
        currentDate={currentDate}
      />
    );
  }

  return null;
}
